import {
    Address,
    FireApiClient,
    GetTariffOptionsParameterObject,
    GetTariffParameterObject,
    GetTariffParameterObjectResidentType,
    MainGuarantee,
} from "@baloise-cfa/tsclient/fire";
import { useWindow } from "@lg-cfa/hooks";
import { generateApiUrl, getCityName } from "@lg-cfa/utils";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import { useFlowNavigation } from "../useFlowNavigation";

import { PageNames } from "@enums";
import {
    baseTariffState,
    optionTariffState,
    propertyStreetState,
} from "@states";
import { insuranceState, userDataState, userTypeState } from "@states/userData";

export function useTariff() {
    const [propertyStreet] = useRecoilState(propertyStreetState);
    const userType = useRecoilValue(userTypeState);
    const data = useRecoilValue(userDataState);

    const chosenInsurance = useRecoilValue(insuranceState);

    const [baseTariff, setBaseTariff] = useRecoilState(baseTariffState);
    const [optionTariff, setOptionTariff] = useRecoilState(optionTariffState);

    const { navigateTo } = useFlowNavigation();

    const [baseLoading, setBaseLoading] = useState<boolean>(true);
    const [optionLoading, setOptionLoading] = useState<boolean>(true);

    const { hasWindow } = useWindow();

    const client = hasWindow
        ? new FireApiClient(generateApiUrl("fire"))
        : undefined;

    const addressObject: Address = {
        city: getCityName(data.propertyCity),
        postalCode: Number(data.propertyPostalCode),
        street: propertyStreet?.name,
        streetId: propertyStreet?.id,
        houseNumber: data.propertyNumber,
        bus: data.propertyLetterBox,
    };

    const baseTariffObject: GetTariffParameterObject = {
        address: addressObject,
        risk: data.propertyRisk,
        residentType: userType as GetTariffParameterObjectResidentType,
        buildingType: data.housingType,
        rooms: {
            garages: data.garages,
            others: data.others,
            attics: data.attics,
            outbuildings: data.extras,
            basements: data.cellars,
        },
    };

    const getBaseTariff = async () => {
        try {
            const res = await client?.getTariffV2(baseTariffObject);
            setBaseLoading(false);
            setBaseTariff(res);
        } catch (err) {
            console.error(err);
            navigateTo(PageNames.TechnicalDifficulties);
        }
    };

    const selectedMainGuarantee = baseTariff?.mainGuarantees?.find(
        (guarantee: MainGuarantee) =>
            guarantee.name?.toLowerCase() === chosenInsurance?.key,
    );

    const getOptionTariff = async () => {
        if (!baseTariff) await getBaseTariff();

        if (!selectedMainGuarantee) return;

        const optionTariffObject: GetTariffOptionsParameterObject = {
            quoteInput: baseTariffObject,
            mainGuarantee: selectedMainGuarantee,
        };

        try {
            const res = await client?.getTariffV2Options(optionTariffObject);
            setOptionLoading(false);
            setOptionTariff(res ?? []);
        } catch (err) {
            console.error(err);
            navigateTo(PageNames.TechnicalDifficulties);
        }
    };

    useEffect(() => {
        if (!baseTariff) {
            getBaseTariff();
        } else {
            if (baseLoading === true) setBaseLoading(false);
        }
        // eslint-disable-next-line
    }, [baseTariff]);

    useEffect(() => {
        if (optionTariff && optionLoading === true) {
            setOptionLoading(false);
        }
        // eslint-disable-next-line
    }, [optionTariff]);

    return {
        baseLoading,
        optionLoading,
        baseTariff,
        optionTariff,
        getOptionTariff,
    };
}
